const BLOB_CACHE_ENTRY_LIMIT = 48
const BLOB_CACHE_TOTAL_BYTES_LIMIT = 192 * 1024 * 1024
const BLOB_CACHE_SINGLE_BYTES_LIMIT = 96 * 1024 * 1024
const BLOB_CACHE_TTL = 10 * 60 * 1000
const DETAIL_CACHE_ENTRY_LIMIT = 24
const DETAIL_CACHE_TTL = 60 * 60 * 1000
const PREVIEW_MAX_ZOOM_SCALE = 2.4
const PREVIEW_PDF_MIN_RENDER_SCALE = 3
const PREVIEW_PDF_SCALE_LIMIT = 8
const PREVIEW_PROGRESS_MIN_DELTA = 2
const PREVIEW_PROGRESS_MIN_INTERVAL = 32
const PREVIEW_ZOOM_MIN_LEVEL = -3
const PREVIEW_ZOOM_MAX_LEVEL = 5
const PREVIEW_ZOOM_STEP = 0.2
const PREVIEW_PAGE_IMAGE_STATE_LIMIT = 160
const PAPER_PDF_FRAME_HEIGHT_WIDTH_RATIO = 4 / 3
const PAPER_ORIGINAL_IMAGE_URL_KEYS = [
  'annotationImageUrl',
  'annotation_image_url',
  'inkImageUrl',
  'ink_image_url',
  'handwritingImageUrl',
  'handwriting_image_url',
  'noteImageUrl',
  'note_image_url',
  'sourceImageUrl',
  'source_image_url',
  'originImageUrl',
  'origin_image_url',
  'originalImageUrl',
  'original_image_url',
  'fullImageUrl',
  'full_image_url',
  'rawImageUrl',
  'raw_image_url',
  'downloadUrl',
  'download_url',
  'originalUrl',
  'original_url',
  'resourceUrl',
  'resource_url',
  'fileUrl',
  'file_url',
  'ossUrl',
  'oss_url',
  'path',
  'filePath',
  'file_path'
]
const PAPER_OVERLAY_IMAGE_URL_KEYS = [
  'annotationImageUrl',
  'annotation_image_url',
  'inkImageUrl',
  'ink_image_url',
  'handwritingImageUrl',
  'handwriting_image_url',
  'noteImageUrl',
  'note_image_url'
]
const PAPER_DISPLAY_IMAGE_URL_KEYS = [
  'imageUrl',
  'image_url',
  'url',
  'src'
]
const PAPER_THUMBNAIL_IMAGE_URL_KEYS = [
  'cover',
  'coverUrl',
  'cover_url',
  'thumbnail',
  'thumbnailUrl',
  'thumbnail_url',
  'thumb',
  'thumbUrl',
  'thumb_url'
]
const PAPER_COVER_HIGH_QUALITY_IMAGE_URL_KEYS = [
  'sourceImageUrl',
  'source_image_url',
  'originImageUrl',
  'origin_image_url',
  'originalImageUrl',
  'original_image_url',
  'fullImageUrl',
  'full_image_url',
  'rawImageUrl',
  'raw_image_url',
  'imageUrl',
  'image_url'
]
const PAPER_PAGE_IMAGE_URL_KEYS = [
  'pageImageUrl',
  'page_image_url',
  'backgroundImageUrl',
  'background_image_url',
  'baseImageUrl',
  'base_image_url',
  'pageUrl',
  'page_url',
  'pageSrc',
  'page_src',
  'bgImageUrl',
  'bg_image_url',
  'backgroundUrl',
  'background_url',
  'baseUrl',
  'base_url',
  'templateImageUrl',
  'template_image_url',
  'templateUrl',
  'template_url',
  'paperImageUrl',
  'paper_image_url',
  'sourcePageImageUrl',
  'source_page_image_url',
  'originPageImageUrl',
  'origin_page_image_url',
  'originalPageImageUrl',
  'original_page_image_url',
  'fullPageImageUrl',
  'full_page_image_url',
  'rawPageImageUrl',
  'raw_page_image_url'
]
const PAPER_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']

const sharedBlobCacheState = {
  entries: new Map(),
  pending: new Map(),
  totalBytes: 0
}

const sharedDetailCacheState = {
  entries: new Map()
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Math.round(Number(value) || 0)))
}

function getHardwareConcurrency() {
  if (typeof navigator === 'undefined') {
    return 0
  }
  return Math.max(0, Number(navigator.hardwareConcurrency) || 0)
}

function getPreviewRenderConcurrency() {
  const hardwareConcurrency = getHardwareConcurrency()
  if (hardwareConcurrency >= 12) {
    return 3
  }
  if (hardwareConcurrency >= 6) {
    return 2
  }
  return 1
}

function getPreviewDevicePixelRatio() {
  if (typeof window === 'undefined') {
    return 1
  }
  return Math.max(1, Math.min(3, Number(window.devicePixelRatio) || 1))
}

function getCleanUrlExtension(url) {
  const cleanPath = String(url || '').split(/[?#]/)[0]
  const fileName = cleanPath.split(/[\\/]/).pop() || ''
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex < 0 || dotIndex == fileName.length - 1) {
    return ''
  }
  return fileName.slice(dotIndex + 1).toLowerCase()
}

function isUsableImageUrlCandidate(url) {
  const value = String(url || '').trim()
  if (!value) {
    return false
  }
  if (/^data:image\//i.test(value) || /^blob:/i.test(value) || /^mock:/i.test(value)) {
    return true
  }
  const extension = getCleanUrlExtension(value)
  if (!extension) {
    return true
  }
  return PAPER_IMAGE_EXTENSIONS.includes(extension)
}

function normalizeWidthAlignedFrameSize(sizes) {
  const validSizes = (Array.isArray(sizes) ? sizes : [])
    .map(size => ({
      width: Math.max(0, Math.round(Number(size && size.width) || 0)),
      height: Math.max(0, Math.round(Number(size && size.height) || 0))
    }))
    .filter(size => size.width > 0 && size.height > 0)
  if (validSizes.length == 0) {
    return null
  }
  const width = validSizes.reduce((maxWidth, size) => Math.max(maxWidth, size.width), 0)
  const height = validSizes.reduce((maxHeight, size) => {
    return Math.max(maxHeight, Math.round(size.height * width / size.width))
  }, 0)
  return width > 0 && height > 0 ? { width, height } : null
}

function normalizePaperPdfFrameSize(size) {
  const width = Math.max(0, Math.round(Number(size && size.width) || 0))
  if (width <= 0) {
    return null
  }
  return {
    width,
    height: Math.max(1, Math.round(width * PAPER_PDF_FRAME_HEIGHT_WIDTH_RATIO)),
    fixedAspectRatio: true
  }
}

export default {
  data() {
    return {
      previewZoomTip: null,
      previewZoomTipTimer: null,
      previewGridZoomLevels: {},
      paperPreviewPageImageStates: {},
      paperPreviewPageImageStateOrder: []
    }
  },
  watch: {
    currentPage(page) {
      if (this.$options.paperPreviewPagePreload) {
        this.warmPaperPreviewPageNavigation(page)
      }
    },
    detailPages() {
      if (this.$options.paperPreviewPagePreload) {
        this.warmPaperPreviewPageNavigation(this.currentPage)
      }
    }
  },
  created() {
    this._paperPreviewPagePreloadEntries = Object.create(null)
  },
  beforeDestroy() {
    this.clearPreviewZoomTip()
    this.clearPaperPreviewPagePreloads()
  },
  methods: {
    getPaperPreviewPageImageUrl(page) {
      return String(page && page.imageUrl || '').trim()
    },
    getPaperPreviewPageImageState(pageOrUrl) {
      const url = typeof pageOrUrl == 'string'
        ? String(pageOrUrl).trim()
        : this.getPaperPreviewPageImageUrl(pageOrUrl)
      return url ? this.paperPreviewPageImageStates[url] || '' : ''
    },
    isPaperPreviewPageImageLoading(page) {
      const url = this.getPaperPreviewPageImageUrl(page)
      if (!url) {
        return false
      }
      const state = this.getPaperPreviewPageImageState(url)
      return state != 'ready' && state != 'error'
    },
    isPaperPreviewNavigationLocked(page = this.currentPage) {
      return !!this.paperDetailLoading || this.isPaperPreviewPageImageLoading(page)
    },
    setPaperPreviewPageImageState(url, state) {
      const normalizedUrl = String(url || '').trim()
      if (!normalizedUrl) {
        return
      }
      if (!Object.prototype.hasOwnProperty.call(this.paperPreviewPageImageStates, normalizedUrl)) {
        this.paperPreviewPageImageStateOrder.push(normalizedUrl)
      }
      this.$set(this.paperPreviewPageImageStates, normalizedUrl, state)
      while (this.paperPreviewPageImageStateOrder.length > PREVIEW_PAGE_IMAGE_STATE_LIMIT) {
        const expiredUrl = this.paperPreviewPageImageStateOrder.shift()
        if (expiredUrl && expiredUrl != normalizedUrl) {
          this.$delete(this.paperPreviewPageImageStates, expiredUrl)
        }
      }
    },
    settlePaperPreviewPageImage(image, urls) {
      const imageUrls = (Array.isArray(urls) ? urls : [urls]).filter(Boolean)
      const markReady = () => imageUrls.forEach(url => this.setPaperPreviewPageImageState(url, 'ready'))
      if (!image || typeof image.decode != 'function') {
        markReady()
        return
      }
      image.decode().catch(() => {}).then(markReady)
    },
    handlePaperPreviewPageImageLoad(event, page) {
      const image = event && event.currentTarget
      const pageUrl = this.getPaperPreviewPageImageUrl(page)
      const loadedUrl = String(image && (image.currentSrc || image.src) || pageUrl).trim()
      this.settlePaperPreviewPageImage(image, [pageUrl, loadedUrl])
    },
    handlePaperPreviewPageImageError(event, page) {
      const image = event && event.currentTarget
      const pageUrl = this.getPaperPreviewPageImageUrl(page)
      const failedUrl = String(image && (image.currentSrc || image.src) || pageUrl).trim()
      ;[pageUrl, failedUrl].filter(Boolean).forEach(url => this.setPaperPreviewPageImageState(url, 'error'))
    },
    preloadPaperPreviewPageImage(page, priority = 'low') {
      const url = this.getPaperPreviewPageImageUrl(page)
      if (!url || typeof Image === 'undefined') {
        return
      }
      const state = this.getPaperPreviewPageImageState(url)
      const entries = this._paperPreviewPagePreloadEntries || (this._paperPreviewPagePreloadEntries = Object.create(null))
      if (state == 'ready' || state == 'error' || entries[url]) {
        return
      }
      const image = new Image()
      entries[url] = image
      image.decoding = 'async'
      image.loading = 'eager'
      if ('fetchPriority' in image) {
        image.fetchPriority = priority
      }
      const release = () => {
        image.onload = null
        image.onerror = null
        if (entries[url] === image) {
          delete entries[url]
        }
      }
      image.onload = () => {
        const markReady = () => {
          this.setPaperPreviewPageImageState(url, 'ready')
          release()
        }
        if (typeof image.decode == 'function') {
          image.decode().catch(() => {}).then(markReady)
        } else {
          markReady()
        }
      }
      image.onerror = () => {
        release()
      }
      image.src = url
    },
    warmPreviewPageImageNavigation(pages, page) {
      const list = Array.isArray(pages) ? pages : []
      if (list.length == 0) {
        return
      }
      let currentIndex = list.indexOf(page)
      if (currentIndex < 0 && page) {
        currentIndex = list.findIndex(item => item && (
          page.id && item.id == page.id ||
          item.pageNo == page.pageNo
        ))
      }
      if (currentIndex < 0) {
        currentIndex = 0
      }
      [0, 1, 2, -1].forEach((offset, preloadIndex) => {
        const targetPage = list[currentIndex + offset]
        if (targetPage) {
          this.preloadPaperPreviewPageImage(targetPage, preloadIndex == 0 ? 'high' : 'low')
        }
      })
    },
    warmPaperPreviewPageNavigation(page = this.currentPage) {
      this.warmPreviewPageImageNavigation(this.detailPages, page)
    },
    clearPaperPreviewPagePreloads() {
      const entries = this._paperPreviewPagePreloadEntries || {}
      Object.keys(entries).forEach(url => {
        const image = entries[url]
        if (image) {
          image.onload = null
          image.onerror = null
        }
        delete entries[url]
      })
    },
    clampPreviewZoomLevel(level) {
      return Math.min(PREVIEW_ZOOM_MAX_LEVEL, Math.max(PREVIEW_ZOOM_MIN_LEVEL, Number(level) || 0))
    },
    getPreviewZoomValue(level) {
      return Number(Math.max(0.1, 1 + this.clampPreviewZoomLevel(level) * PREVIEW_ZOOM_STEP).toFixed(3))
    },
    getPreviewZoomPercent(level) {
      return Math.round(this.getPreviewZoomValue(level) * 100)
    },
    getPreviewZoomActionLabel(direction, labels = {}) {
      const source = labels && typeof labels == 'object' ? labels : {}
      const key = direction == 'out' ? 'zoomOut' : 'zoomIn'
      return source[key] || (this.ui && this.ui[key]) || (this.uiText && this.uiText[key]) || (direction == 'out' ? '缩小' : '放大')
    },
    getPreviewZoomTipText(direction, level, labels = {}) {
      return `${this.getPreviewZoomActionLabel(direction, labels)} ${this.getPreviewZoomPercent(level)}%`
    },
    showPreviewZoomTip(scope, direction, level, labels = {}) {
      const normalizedScope = String(scope || 'preview')
      const normalizedDirection = direction == 'out' ? 'out' : 'in'
      const normalizedLevel = this.clampPreviewZoomLevel(level)
      const text = this.getPreviewZoomTipText(normalizedDirection, normalizedLevel, labels)
      this.clearPreviewZoomTipTimer()
      this.previewZoomTip = {
        scope: normalizedScope,
        direction: normalizedDirection,
        text
      }
      this.previewZoomTipTimer = setTimeout(() => {
        this.clearPreviewZoomTip()
      }, 5000)
    },
    clearPreviewZoomTipTimer() {
      if (this.previewZoomTipTimer) {
        clearTimeout(this.previewZoomTipTimer)
        this.previewZoomTipTimer = null
      }
    },
    clearPreviewZoomTip() {
      this.clearPreviewZoomTipTimer()
      this.previewZoomTip = null
    },
    rememberPreviewGridZoomLevel(scope, level) {
      const normalizedScope = String(scope || 'preview')
      this.previewGridZoomLevels[normalizedScope] = this.clampPreviewZoomLevel(level)
    },
    takePreviewGridZoomLevel(scope) {
      const normalizedScope = String(scope || 'preview')
      if (!Object.prototype.hasOwnProperty.call(this.previewGridZoomLevels, normalizedScope)) {
        return 0
      }
      const level = this.previewGridZoomLevels[normalizedScope]
      delete this.previewGridZoomLevels[normalizedScope]
      return this.clampPreviewZoomLevel(level)
    },
    isPreviewZoomTipVisible(scope, direction) {
      if (!this.previewZoomTip) {
        return false
      }
      return this.previewZoomTip.scope == String(scope || 'preview') && this.previewZoomTip.direction == (direction == 'out' ? 'out' : 'in')
    },
    getPaperPreviewRenderConcurrency() {
      return getPreviewRenderConcurrency()
    },
    getPaperPreviewRenderScale(baseViewport, targetSize, options = {}) {
      const baseWidth = Math.max(1, Number(baseViewport && baseViewport.width) || 1)
      const baseHeight = Math.max(1, Number(baseViewport && baseViewport.height) || 1)
      const targetWidth = Number(targetSize && targetSize.width) || 0
      const targetHeight = Number(targetSize && targetSize.height) || 0
      const widthScale = targetWidth > 0 ? targetWidth / baseWidth : 0
      const heightScale = targetHeight > 0 ? targetHeight / baseHeight : 0
      const previewHeight = typeof window === 'undefined' ? 0 : Math.max(0, Number(window.innerHeight) || 0)
      const displayScale = options.includeDisplayScale !== false && previewHeight > 0
        ? previewHeight * getPreviewDevicePixelRatio() * PREVIEW_MAX_ZOOM_SCALE / baseHeight
        : 0
      const sourceScale = Math.max(widthScale, heightScale)
      if (sourceScale > 0) {
        return Math.max(PREVIEW_PDF_MIN_RENDER_SCALE, sourceScale, displayScale)
      }
      return Math.min(PREVIEW_PDF_SCALE_LIMIT, Math.max(PREVIEW_PDF_MIN_RENDER_SCALE, displayScale))
    },
    pickPaperPreviewString(source, keys) {
      if (!source || typeof source != 'object') {
        return ''
      }
      if (typeof this.pickString == 'function') {
        return this.pickString(source, keys)
      }
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        const value = source[key]
        if (value !== undefined && value !== null && value !== '') {
          return String(value).trim()
        }
      }
      return ''
    },
    pickPaperBestImageUrl(source, options = {}) {
      if (typeof source == 'string') {
        return isUsableImageUrlCandidate(source) ? String(source).trim() : ''
      }
      if (!source || typeof source != 'object') {
        return ''
      }
      const includeDisplay = options.includeDisplay !== false
      const includeThumbnail = options.includeThumbnail !== false
      const keyGroups = Array.isArray(options.keys) && options.keys.length > 0
        ? [options.keys]
        : [PAPER_ORIGINAL_IMAGE_URL_KEYS]
      if (!(Array.isArray(options.keys) && options.keys.length > 0)) {
        if (includeDisplay) {
          keyGroups.push(PAPER_DISPLAY_IMAGE_URL_KEYS)
        }
        if (includeThumbnail) {
          keyGroups.push(PAPER_THUMBNAIL_IMAGE_URL_KEYS)
        }
      }
      for (let i = 0; i < keyGroups.length; i += 1) {
        const url = this.pickPaperPreviewString(source, keyGroups[i])
        if (isUsableImageUrlCandidate(url)) {
          return url
        }
      }
      return ''
    },
    pickPaperCoverImageUrl(source) {
      return this.pickPaperBestImageUrl(source, {
        keys: PAPER_COVER_HIGH_QUALITY_IMAGE_URL_KEYS
      }) || this.pickPaperBestImageUrl(source, {
        keys: PAPER_THUMBNAIL_IMAGE_URL_KEYS
      })
    },
    getPaperGridCoverImageUrl(item) {
      if (!item || item.gridFallbackCoverFailed) {
        return ''
      }
      const gridCover = String(item.gridCover || '').trim()
      const cover = String(item.cover || '').trim()
      if (gridCover && gridCover !== cover && !item.gridCoverFailed) {
        return gridCover
      }
      return cover
    },
    markPaperGridCoverFailed(item) {
      if (!item) {
        return
      }
      const gridCover = String(item.gridCover || '').trim()
      const cover = String(item.cover || '').trim()
      const key = gridCover && gridCover !== cover && !item.gridCoverFailed
        ? 'gridCoverFailed'
        : 'gridFallbackCoverFailed'
      if (typeof this.$set == 'function') {
        this.$set(item, key, true)
      } else {
        item[key] = true
      }
    },
    pickPaperPageImageUrl(source) {
      return this.pickPaperBestImageUrl(source, {
        keys: PAPER_PAGE_IMAGE_URL_KEYS
      })
    },
    pickPaperOverlayImageUrl(source) {
      return this.pickPaperBestImageUrl(source, {
        keys: PAPER_OVERLAY_IMAGE_URL_KEYS
      })
    },
    getPaperPreviewFrameScale(frameSize, includeDisplayScale = true) {
      const frameHeight = Math.max(1, Number(frameSize && frameSize.height) || 1)
      if (!includeDisplayScale || typeof window === 'undefined') {
        return 1
      }
      const previewHeight = Math.max(0, Number(window.innerHeight) || 0)
      if (!previewHeight) {
        return 1
      }
      return Math.max(1, previewHeight * getPreviewDevicePixelRatio() * PREVIEW_MAX_ZOOM_SCALE / frameHeight)
    },
    getPaperOverlayCompositeOperation(image) {
      if (typeof document === 'undefined' || !image) {
        return 'source-over'
      }
      const width = Math.max(1, Math.round(image.naturalWidth || image.width || 0))
      const height = Math.max(1, Math.round(image.naturalHeight || image.height || 0))
      if (!(width > 0 && height > 0)) {
        return 'source-over'
      }
      const maxSampleSize = 160
      const sampleScale = Math.min(1, maxSampleSize / Math.max(width, height))
      const sampleWidth = Math.max(1, Math.round(width * sampleScale))
      const sampleHeight = Math.max(1, Math.round(height * sampleScale))
      const canvas = document.createElement('canvas')
      canvas.width = sampleWidth
      canvas.height = sampleHeight
      try {
        const context = canvas.getContext('2d')
        context.drawImage(image, 0, 0, sampleWidth, sampleHeight)
        const data = context.getImageData(0, 0, sampleWidth, sampleHeight).data
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 250) {
            return 'source-over'
          }
        }
        return 'multiply'
      } catch (error) {
        return 'source-over'
      } finally {
        canvas.width = 0
        canvas.height = 0
      }
    },
    getPaperWidthAlignedFrameSize(sizes) {
      return normalizeWidthAlignedFrameSize(sizes)
    },
    async getUnifiedPaperCompositeFrameSize(pages, task, onProgress) {
      if (task && task.compositeFrameSize && task.compositeFrameSize.width > 0 && task.compositeFrameSize.height > 0) {
        return task.compositeFrameSize
      }
      const imageUrls = Array.from(new Set((Array.isArray(pages) ? pages : [])
        .reduce((urls, page) => {
          if (page && page.imageUrl) {
            urls.push(String(page.imageUrl).trim())
          }
          if (page && page.pageImageUrl) {
            urls.push(String(page.pageImageUrl).trim())
          }
          return urls
        }, [])
        .filter(Boolean)))
      if (imageUrls.length == 0 || typeof this.fetchPaperImageBlob != 'function' || typeof this.getPaperImageBlobSize != 'function') {
        return null
      }
      const updateProgress = this.createAggregatedProgressReporter(onProgress, imageUrls.length, 0, 100)
      const sizes = await this.runPaperPerformanceTasks(
        imageUrls,
        this.getPaperPreviewRenderConcurrency(),
        async (imageUrl, index) => {
          try {
            const blob = await this.fetchPaperImageBlob(imageUrl, percent => updateProgress(index, percent), task)
            const size = await this.getPaperImageBlobSize(blob)
            updateProgress(index, 100)
            return size
          } catch (error) {
            if (task && task.cancelled) {
              throw error
            }
            updateProgress(index, 100)
            return null
          }
        }
      )
      const frameSize = normalizeWidthAlignedFrameSize(sizes)
      if (!(frameSize && frameSize.width > 0 && frameSize.height > 0)) {
        return null
      }
      if (task) {
        if (typeof this.$set == 'function') {
          this.$set(task, 'compositeFrameSize', frameSize)
        } else {
          task.compositeFrameSize = frameSize
        }
      }
      return frameSize
    },
    async getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task, onProgress) {
      if (task && task.compositeFrameSize && task.compositeFrameSize.width > 0 && task.compositeFrameSize.height > 0) {
        return task.compositeFrameSize
      }
      const reportProgress = this.createMonotonicProgressReporter(onProgress, 0)
      const imageFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, null, percent => {
        reportProgress((Number(percent) || 0) * 0.8)
      })
      const allSizes = imageFrameSize ? [imageFrameSize] : []
      if (pdfDocument && pdfDocument.numPages) {
        const pageNumbers = Array.from(new Set((Array.isArray(pages) ? pages : [])
          .map((page, index) => Number(page && page.pageNo) || index + 1)
          .filter(pageNo => pageNo > 0 && pageNo <= pdfDocument.numPages)))
        const sizes = await this.runPaperPerformanceTasks(
          pageNumbers,
          this.getPaperPreviewRenderConcurrency(),
          async (pageNo, index) => {
            const page = await pdfDocument.getPage(pageNo)
            const viewport = page.getViewport({ scale: 1 })
            const renderScale = this.getPaperPreviewRenderScale(viewport, null, { includeDisplayScale: false })
            const size = {
              width: Math.ceil(viewport.width * renderScale),
              height: Math.ceil(viewport.height * renderScale)
            }
            if (typeof page.cleanup == 'function') {
              page.cleanup()
            }
            reportProgress(80 + ((index + 1) / Math.max(pageNumbers.length, 1)) * 20)
            return size
          }
        )
        allSizes.push(...sizes)
      }
      reportProgress(100)
      const frameSize = normalizePaperPdfFrameSize(normalizeWidthAlignedFrameSize(allSizes))
      if (!(frameSize && frameSize.width > 0 && frameSize.height > 0)) {
        return null
      }
      if (task) {
        if (typeof this.$set == 'function') {
          this.$set(task, 'compositeFrameSize', frameSize)
        } else {
          task.compositeFrameSize = frameSize
        }
      }
      return frameSize
    },
    fitPaperCompositeCanvasToFrame(canvas, frameSize, outputScale = 1, options = {}) {
      const frameWidth = Math.max(0, Math.round(Number(frameSize && frameSize.width) || 0))
      const frameHeight = Math.max(0, Math.round(Number(frameSize && frameSize.height) || 0))
      if (!canvas || !(frameWidth > 0 && frameHeight > 0)) {
        return canvas
      }
      const safeOutputScale = Math.max(1, Number(outputScale) || 1)
      const targetWidth = Math.max(1, Math.round(frameWidth * safeOutputScale))
      const targetHeight = Math.max(1, Math.round(frameHeight * safeOutputScale))
      const strictTargetHeight = !!(frameSize && frameSize.fixedAspectRatio)
      if (canvas.width == targetWidth && canvas.height == targetHeight) {
        return canvas
      }
      const sourceCanvas = document.createElement('canvas')
      sourceCanvas.width = Math.max(1, canvas.width || targetWidth)
      sourceCanvas.height = Math.max(1, canvas.height || targetHeight)
      sourceCanvas.getContext('2d').drawImage(canvas, 0, 0)
      const rect = this.getPaperWidthAlignedDrawRect(sourceCanvas.width, sourceCanvas.height, targetWidth, targetHeight, {
        strictTargetHeight,
        allowHeightFallback: !!(options && options.allowHeightFallback)
      })
      canvas.width = rect.targetWidth
      canvas.height = rect.targetHeight
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(sourceCanvas, rect.x, rect.y, rect.drawWidth, rect.drawHeight)
      sourceCanvas.width = 0
      sourceCanvas.height = 0
      return canvas
    },
    getPaperWidthAlignedDrawRect(sourceWidth, sourceHeight, targetWidth, targetHeight, options = {}) {
      const safeSourceWidth = Math.max(1, Math.round(Number(sourceWidth) || 1))
      const safeSourceHeight = Math.max(1, Math.round(Number(sourceHeight) || 1))
      const safeTargetWidth = Math.max(1, Math.round(Number(targetWidth) || safeSourceWidth))
      const scale = safeTargetWidth / safeSourceWidth
      const drawWidth = safeTargetWidth
      const drawHeight = Math.max(1, Math.round(safeSourceHeight * scale))
      const requestedTargetHeight = Math.max(1, Math.round(Number(targetHeight) || drawHeight))
      if (options && options.allowHeightFallback && drawHeight > requestedTargetHeight) {
        const heightScale = requestedTargetHeight / safeSourceHeight
        const heightDrawWidth = Math.max(1, Math.round(safeSourceWidth * heightScale))
        return {
          targetWidth: safeTargetWidth,
          targetHeight: requestedTargetHeight,
          drawWidth: heightDrawWidth,
          drawHeight: requestedTargetHeight,
          x: Math.round((safeTargetWidth - heightDrawWidth) / 2),
          y: 0
        }
      }
      const finalTargetHeight = options && options.strictTargetHeight
        ? requestedTargetHeight
        : Math.max(requestedTargetHeight, drawHeight)
      return {
        targetWidth: safeTargetWidth,
        targetHeight: finalTargetHeight,
        drawWidth,
        drawHeight,
        x: 0,
        y: Math.round((finalTargetHeight - drawHeight) / 2)
      }
    },
    createPaperImageFrameCanvas(blob, frameSize, outputScale = 1) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = Math.max(1, Math.round(image.naturalWidth || image.width || 0))
          const height = Math.max(1, Math.round(image.naturalHeight || image.height || 0))
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)
          URL.revokeObjectURL(url)
          resolve(this.fitPaperCompositeCanvasToFrame(canvas, frameSize, outputScale))
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    trimPaperPreviewDetailCache(cache, keepKey, limit = 3) {
      if (!cache || typeof cache != 'object') {
        return
      }
      const maxEntries = Math.max(1, Number(limit) || 1)
      const keys = Object.keys(cache)
        .filter(key => key != keepKey)
        .sort((left, right) => {
          return (Number(cache[left] && cache[left].cachedAt) || 0) - (Number(cache[right] && cache[right].cachedAt) || 0)
        })
      while (Object.keys(cache).length > maxEntries && keys.length > 0) {
        const key = keys.shift()
        const entry = cache[key]
        if (entry && typeof this.revokePaperPreviewObjectUrls == 'function') {
          this.revokePaperPreviewObjectUrls(entry.objectUrls)
        }
        if (typeof this.$delete == 'function') {
          this.$delete(cache, key)
        } else {
          delete cache[key]
        }
      }
    },
    getPreviewPerformanceDetailCacheState() {
      return sharedDetailCacheState
    },
    normalizePaperPreviewDetailCacheKey(scope, cacheKey) {
      const key = String(cacheKey || '').trim()
      if (!key) {
        return ''
      }
      const scopeName = String(scope || (this.$options && this.$options.name) || 'paper-preview').trim() || 'paper-preview'
      return `${scopeName}:${key}`
    },
    getSharedPaperPreviewDetailCache(scope, cacheKey) {
      const normalizedKey = this.normalizePaperPreviewDetailCacheKey(scope, cacheKey)
      if (!normalizedKey) {
        return null
      }
      const state = this.getPreviewPerformanceDetailCacheState()
      const cached = state.entries.get(normalizedKey)
      if (!cached || !cached.detail) {
        return null
      }
      if (Date.now() - (Number(cached.cachedAt) || 0) > DETAIL_CACHE_TTL) {
        state.entries.delete(normalizedKey)
        this.revokePaperPreviewObjectUrlsSafely(cached.objectUrls)
        return null
      }
      state.entries.delete(normalizedKey)
      cached.touchedAt = Date.now()
      state.entries.set(normalizedKey, cached)
      return cached
    },
    setSharedPaperPreviewDetailCache(scope, cacheKey, detail, objectUrls) {
      const normalizedKey = this.normalizePaperPreviewDetailCacheKey(scope, cacheKey)
      if (!normalizedKey || !detail) {
        return null
      }
      const state = this.getPreviewPerformanceDetailCacheState()
      const previous = state.entries.get(normalizedKey)
      if (previous) {
        state.entries.delete(normalizedKey)
        if (previous.objectUrls !== objectUrls) {
          this.revokePaperPreviewObjectUrlsSafely(previous.objectUrls)
        }
      }
      const entry = {
        detail,
        objectUrls: Array.isArray(objectUrls) ? objectUrls.slice() : [],
        cachedAt: Date.now(),
        touchedAt: Date.now()
      }
      state.entries.set(normalizedKey, entry)
      this.trimSharedPaperPreviewDetailCache(normalizedKey)
      return entry
    },
    trimSharedPaperPreviewDetailCache(keepKey = '') {
      const state = this.getPreviewPerformanceDetailCacheState()
      const now = Date.now()
      Array.from(state.entries.keys()).forEach(key => {
        if (key == keepKey) {
          return
        }
        const entry = state.entries.get(key)
        if (entry && now - (Number(entry.cachedAt) || 0) > DETAIL_CACHE_TTL) {
          state.entries.delete(key)
          this.revokePaperPreviewObjectUrlsSafely(entry.objectUrls)
        }
      })
      while (state.entries.size > DETAIL_CACHE_ENTRY_LIMIT) {
        const removableKey = Array.from(state.entries.keys()).find(key => key != keepKey)
        if (!removableKey) {
          break
        }
        const entry = state.entries.get(removableKey)
        state.entries.delete(removableKey)
        this.revokePaperPreviewObjectUrlsSafely(entry && entry.objectUrls)
      }
    },
    isPaperPreviewObjectUrlShared(url) {
      const value = String(url || '')
      if (!value || !value.startsWith('blob:')) {
        return false
      }
      const state = this.getPreviewPerformanceDetailCacheState()
      for (const entry of state.entries.values()) {
        const urls = Array.isArray(entry && entry.objectUrls) ? entry.objectUrls : []
        if (urls.includes(value)) {
          return true
        }
      }
      return false
    },
    revokePaperPreviewObjectUrlsSafely(urls) {
      const list = Array.isArray(urls) ? urls : []
      list.forEach(url => {
        const value = String(url || '')
        if (value && value.startsWith('blob:') && !this.isPaperPreviewObjectUrlShared(value)) {
          URL.revokeObjectURL(value)
        }
      })
    },
    createMonotonicProgressReporter(handler, initialValue = 0) {
      let lastValue = clampPercent(initialValue)
      let lastEmittedValue = lastValue
      let lastEmitAt = 0
      return value => {
        const nextValue = Math.max(lastValue, clampPercent(value))
        lastValue = nextValue
        if (typeof handler != 'function' || nextValue == lastEmittedValue) {
          return
        }
        const now = Date.now()
        const shouldEmit = nextValue >= 100
          || nextValue - lastEmittedValue >= PREVIEW_PROGRESS_MIN_DELTA
          || now - lastEmitAt >= PREVIEW_PROGRESS_MIN_INTERVAL
        if (shouldEmit) {
          lastEmittedValue = nextValue
          lastEmitAt = now
          handler(nextValue)
        }
      }
    },
    createAggregatedProgressReporter(handler, itemCount, startProgress = 0, endProgress = 100) {
      const totalItems = Math.max(1, Number(itemCount) || 0)
      const progressValues = new Array(totalItems).fill(0)
      let totalPercent = 0
      const reportProgress = this.createMonotonicProgressReporter(handler, startProgress)
      return (index, value) => {
        if (!(index >= 0 && index < totalItems)) {
          return
        }
        const previousValue = progressValues[index]
        const nextValue = Math.max(previousValue, clampPercent(value))
        if (nextValue == previousValue) {
          return
        }
        progressValues[index] = nextValue
        totalPercent += nextValue - previousValue
        const averagePercent = totalPercent / totalItems
        const mappedPercent = startProgress + ((endProgress - startProgress) * averagePercent / 100)
        reportProgress(mappedPercent)
      }
    },
    waitForPaperPreviewTaskYield() {
      if (typeof window === 'undefined') {
        return Promise.resolve()
      }
      return new Promise(resolve => {
        setTimeout(resolve, 0)
      })
    },
    hasPaperPreviewFrameSize(frameSize) {
      return !!(frameSize && Number(frameSize.width) > 0 && Number(frameSize.height) > 0)
    },
    async runPaperPerformanceTasks(items, limit, worker, onItemComplete) {
      const list = Array.isArray(items) ? items.slice() : []
      if (list.length == 0 || typeof worker != 'function') {
        return []
      }
      const concurrency = Math.max(1, Math.min(Number(limit) || 1, list.length))
      const results = new Array(list.length)
      let cursor = 0
      const runNext = async () => {
        while (cursor < list.length) {
          const index = cursor
          cursor += 1
          const result = await worker(list[index], index)
          results[index] = result
          if (typeof onItemComplete == 'function') {
            await onItemComplete(result, index, list[index])
          }
          if (cursor < list.length && typeof this.waitForPaperPreviewTaskYield == 'function') {
            await this.waitForPaperPreviewTaskYield()
          }
        }
      }
      await Promise.all(Array.from({ length: concurrency }, () => runNext()))
      return results
    },
    async getPreviewPerformanceImageBlobSize(blob) {
      if (!blob) {
        return null
      }
      if (typeof window === 'undefined' || typeof window.createImageBitmap != 'function') {
        return null
      }
      let bitmap = null
      try {
        bitmap = await window.createImageBitmap(blob)
        const width = Number(bitmap && bitmap.width) || 0
        const height = Number(bitmap && bitmap.height) || 0
        return width > 0 && height > 0 ? { width, height } : null
      } catch (error) {
        return null
      } finally {
        if (bitmap && typeof bitmap.close == 'function') {
          bitmap.close()
        }
      }
    },
    async getPaperImageBlobSize(blob) {
      const bitmapSize = await this.getPreviewPerformanceImageBlobSize(blob)
      if (bitmapSize) {
        return bitmapSize
      }
      return new Promise(resolve => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 0
          const height = image.naturalHeight || image.height || 0
          URL.revokeObjectURL(url)
          resolve(width > 0 && height > 0 ? { width, height } : null)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(null)
        }
        image.src = url
      })
    },
    releasePaperPreviewCanvasWhenReady(canvasPromise) {
      if (!canvasPromise || typeof canvasPromise.then != 'function') {
        return
      }
      canvasPromise.then(canvas => {
        if (canvas && typeof this.releaseCanvas == 'function') {
          this.releaseCanvas(canvas)
        }
      }).catch(() => {})
    },
    createRenderedPaperPreviewPage(pageInfo, imageUrl, extra = {}) {
      const pageNo = typeof pageInfo == 'object'
        ? (Number(pageInfo && pageInfo.pageNo) || 0)
        : (Number(pageInfo) || 0)
      return Object.assign({
        pageNo: pageNo || 1,
        imageUrl: imageUrl || '',
        isRenderedPreview: true
      }, extra)
    },
    isRenderedPaperPreviewPage(page) {
      return !!(page && page.isRenderedPreview && page.imageUrl)
    },
    areRenderedPaperPreviewPages(pages) {
      const list = Array.isArray(pages) ? pages.filter(Boolean) : []
      return list.length > 0 && list.every(page => this.isRenderedPaperPreviewPage(page))
    },
    async buildRenderedPaperPreviewPages(options = {}) {
      const pages = Array.isArray(options.pages) ? options.pages : []
      const objectUrls = Array.isArray(options.objectUrls) ? options.objectUrls : []
      const startProgress = clampPercent(options.startProgress)
      const endProgress = clampPercent(options.endProgress)
      const renderPage = typeof options.renderPage == 'function' ? options.renderPage : null
      if (pages.length == 0 || !renderPage) {
        return []
      }
      const updateProgress = this.createAggregatedProgressReporter(options.onProgress, pages.length, startProgress, endProgress)
      const imagePages = new Array(pages.length)
      await this.runPaperPerformanceTasks(
        pages,
        this.getPaperPreviewRenderConcurrency(),
        async (page, index) => {
          const blob = await renderPage(page, index, pages.length, percent => {
            updateProgress(index, percent)
          })
          return {
            index,
            pageNo: Number(page && page.pageNo) || index + 1,
            blob,
            hasSourceOverlay: !!(page && (page.overlayImageUrl || page.imageUrl)),
            sourcePage: page && typeof page == 'object' ? Object.assign({}, page) : null
          }
        },
        result => {
          const url = URL.createObjectURL(result.blob)
          objectUrls.push(url)
          imagePages[result.index] = this.createRenderedPaperPreviewPage(result.pageNo, url, {
            hasSourceOverlay: !!result.hasSourceOverlay,
            sourcePage: result.sourcePage || null
          })
          updateProgress(result.index, 100)
        }
      )
      return imagePages
    },
    hasPaperPdfSourceOverlays(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages.some(page => {
        if (!page) {
          return false
        }
        if (page.hasSourceOverlay) {
          return true
        }
        return !page.isRenderedPreview && !!(page.imageUrl || page.pageImageUrl)
      })
    },
    getPreviewPerformanceBlobCacheState() {
      return sharedBlobCacheState
    },
    clearPreviewPerformanceBlobCache() {
      sharedBlobCacheState.entries.clear()
      sharedBlobCacheState.pending.clear()
      sharedBlobCacheState.totalBytes = 0
    },
    normalizePreviewPerformanceBlobCacheKey(url) {
      return String(url || '').trim()
    },
    shouldCachePreviewPerformanceBlob(cacheKey, blob) {
      if (!cacheKey || /^data:/i.test(cacheKey) || !blob) {
        return false
      }
      const size = Number(blob.size) || 0
      return size > 0 && size <= BLOB_CACHE_SINGLE_BYTES_LIMIT
    },
    getPreviewPerformanceTaskAbortState(task) {
      if (!task) {
        return null
      }
      if (!task.__previewPerformanceAbortState) {
        task.__previewPerformanceAbortState = {
          controllers: new Set(),
          proxy: null
        }
      }
      return task.__previewPerformanceAbortState
    },
    registerPreviewPerformanceTaskAbortController(task, controller) {
      if (!task || !controller) {
        return null
      }
      const state = this.getPreviewPerformanceTaskAbortState(task)
      state.controllers.add(controller)
      if (!state.proxy) {
        state.proxy = {
          abort: () => {
            Array.from(state.controllers).forEach(activeController => {
              if (activeController && typeof activeController.abort == 'function') {
                activeController.abort()
              }
            })
          }
        }
      }
      if (task.abortController !== state.proxy) {
        this.$set(task, 'abortController', state.proxy)
      }
      return state.proxy
    },
    unregisterPreviewPerformanceTaskAbortController(task, controller) {
      if (!task || !task.__previewPerformanceAbortState) {
        return
      }
      const state = task.__previewPerformanceAbortState
      if (controller) {
        state.controllers.delete(controller)
      }
      if (state.controllers.size > 0) {
        if (task.abortController !== state.proxy) {
          this.$set(task, 'abortController', state.proxy)
        }
        return
      }
      if (task.abortController === state.proxy) {
        this.$set(task, 'abortController', null)
      }
      task.__previewPerformanceAbortState = null
    },
    rememberPreviewPerformanceBlob(cacheKey, blob) {
      const normalizedKey = this.normalizePreviewPerformanceBlobCacheKey(cacheKey)
      if (!this.shouldCachePreviewPerformanceBlob(normalizedKey, blob)) {
        return blob
      }
      const state = this.getPreviewPerformanceBlobCacheState()
      if (state.entries.has(normalizedKey)) {
        const existing = state.entries.get(normalizedKey)
        state.totalBytes = Math.max(0, state.totalBytes - (Number(existing && existing.size) || 0))
        state.entries.delete(normalizedKey)
      }
      const size = Number(blob.size) || 0
      state.entries.set(normalizedKey, {
        blob,
        size,
        cachedAt: Date.now(),
        touchedAt: Date.now()
      })
      state.totalBytes += size
      this.trimPreviewPerformanceBlobCache()
      return blob
    },
    getRememberedPreviewPerformanceBlob(cacheKey) {
      const normalizedKey = this.normalizePreviewPerformanceBlobCacheKey(cacheKey)
      if (!normalizedKey) {
        return null
      }
      const state = this.getPreviewPerformanceBlobCacheState()
      const cached = state.entries.get(normalizedKey)
      if (!cached || !cached.blob) {
        return null
      }
      if (Date.now() - (Number(cached.cachedAt) || 0) > BLOB_CACHE_TTL) {
        state.totalBytes = Math.max(0, state.totalBytes - (Number(cached.size) || 0))
        state.entries.delete(normalizedKey)
        return null
      }
      state.entries.delete(normalizedKey)
      cached.touchedAt = Date.now()
      state.entries.set(normalizedKey, cached)
      return cached.blob
    },
    trimPreviewPerformanceBlobCache() {
      const state = this.getPreviewPerformanceBlobCacheState()
      while (state.entries.size > BLOB_CACHE_ENTRY_LIMIT || state.totalBytes > BLOB_CACHE_TOTAL_BYTES_LIMIT) {
        const oldestKey = state.entries.keys().next()
        if (!oldestKey || oldestKey.done) {
          break
        }
        const entry = state.entries.get(oldestKey.value)
        state.totalBytes = Math.max(0, state.totalBytes - (Number(entry && entry.size) || 0))
        state.entries.delete(oldestKey.value)
      }
    },
    async fetchCachedPreviewPerformanceBlob(url, loader, options = {}) {
      const cacheKey = this.normalizePreviewPerformanceBlobCacheKey(options.cacheKey || url)
      if (!cacheKey || typeof loader != 'function') {
        return loader ? loader(options.onProgress) : null
      }
      const cachedBlob = this.getRememberedPreviewPerformanceBlob(cacheKey)
      if (cachedBlob) {
        if (typeof options.onProgress == 'function') {
          options.onProgress(100)
        }
        return cachedBlob
      }
      const state = this.getPreviewPerformanceBlobCacheState()
      const pending = state.pending.get(cacheKey)
      if (pending) {
        const blob = await pending
        if (typeof options.onProgress == 'function') {
          options.onProgress(100)
        }
        return blob
      }
      const pendingPromise = Promise.resolve()
        .then(() => loader(options.onProgress))
        .then(blob => this.rememberPreviewPerformanceBlob(cacheKey, blob))
      if (!options.task) {
        state.pending.set(cacheKey, pendingPromise)
      }
      try {
        const blob = await pendingPromise
        if (typeof options.onProgress == 'function') {
          options.onProgress(100)
        }
        return blob
      } finally {
        if (!options.task && state.pending.get(cacheKey) === pendingPromise) {
          state.pending.delete(cacheKey)
        }
      }
    }
  }
}
